import { DateUtils, RemoteConfigService } from '@web_opxp/features';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import { ASSESSMENT_DOMAIN_ID, ASSESSMENT_SESSION_ID, TRAINING_ID } from '../base/constants';
import ENV from '../base/env';
import TrainingConfig from '../base/training-config.json';
import { IAssessmentConfigOPXP, ITrainingIntensity } from '../base/types';
import { isChallengeWorkoutId } from '../base/utils';
import {
  CyclesError,
  ETrainingStatus,
  IClientDBSync,
  ICycleClientStatus,
  ICycleStatus,
  ISessionClientStatus,
  ISessionStatus,
  IWorkoutClientStatus,
  IWorkoutStatus,
  TCycleSyncMap,
} from './types';

export function createSessionStatusClient(
  cycleId: number,
  sessionId: number,
  domainId: number,
  status: ETrainingStatus,
  finishedTimestamp: number,
  syncTimestamp: number
) {
  const sessionStatus: ISessionClientStatus = {
    cycleId,
    sessionId,
    domainId,
    status,
    finishedTimestamp,
    syncTimestamp,
  };
  return sessionStatus;
}

export function convertSessionStatusFromApi(cycle: ICycleStatus, syncTimestamp: number = DateUtils.getUnixTimestamp()) {
  return cycle.sessions.map(({ sessionId, domainId, status, finishedTimestamp }) =>
    createSessionStatusClient(cycle.cycleId, sessionId, domainId, status, finishedTimestamp, syncTimestamp)
  );
}

export function createWorkoutStatusClient(
  cycleId: number,
  sessionId: number,
  workoutId: number,
  index: number,
  status: ETrainingStatus,
  finishedTimestamp: number,
  syncTimestamp: number
) {
  const workoutStatus: IWorkoutClientStatus = {
    cycleId,
    sessionId,
    finishedTimestamp,
    index,
    status,
    workoutId,
    syncTimestamp,
  };
  return workoutStatus;
}

export function convertWorkoutsStatusFromApi(
  cycle: ICycleStatus,
  syncTimestamp: number = DateUtils.getUnixTimestamp()
) {
  const workouts = flatMap(cycle.sessions, (session) =>
    map(session.workouts, (workout) => {
      const { finishedTimestamp, index, status, workoutId } = workout;
      return createWorkoutStatusClient(
        cycle.cycleId,
        session.sessionId,
        workoutId,
        index,
        status,
        finishedTimestamp,
        syncTimestamp
      );
    })
  );
  return workouts;
}

export const isSessionInactive = (session?: ISessionClientStatus) =>
  session ? session.status === ETrainingStatus.Finished || session.status === ETrainingStatus.Skipped : true;

export const isSessionFinished = (session?: ISessionClientStatus) =>
  session ? session.status === ETrainingStatus.Finished && session.finishedTimestamp !== 0 : true;

export const isSessionSkipped = (session?: ISessionClientStatus) =>
  session ? session.status === ETrainingStatus.Skipped && session.finishedTimestamp !== 0 : true;

export const isSessionActive = (session?: ISessionClientStatus) =>
  !isSessionFinished(session) && !isSessionSkipped(session);

export const createCycleClientStatus = (
  cycleId: number,
  finishedTimestamp: number,
  startTimestamp: number,
  syncTimestamp: number = DateUtils.getUnixTimestamp()
) => {
  const cycle: ICycleClientStatus = {
    cycleId,
    finishedTimestamp,
    startTimestamp,
    syncTimestamp,
    trainingId: TRAINING_ID,
  };
  return cycle;
};

export const convertWorkoutStatusToApi = (clientData: IWorkoutClientStatus) => {
  const { finishedTimestamp, index, status, workoutId } = clientData;
  const workout: IWorkoutStatus = {
    workoutId,
    index,
    finishedTimestamp,
    status,
  };
  return workout;
};

export const convertSessionStatusToApi = (
  sessionData: ISessionClientStatus,
  sessionWorkouts: IWorkoutClientStatus[]
) => {
  const { domainId, finishedTimestamp, sessionId, status } = sessionData;
  const workouts = sessionWorkouts.map(convertWorkoutStatusToApi);
  const session: ISessionStatus = {
    domainId,
    finishedTimestamp,
    sessionId,
    status,
    workouts,
  };
  return session;
};

export const convertCycleStatusToApi = (cycle: ICycleClientStatus, sessions: ISessionStatus[]) => {
  const { cycleId, finishedTimestamp, startTimestamp, trainingId } = cycle;
  const cycleStatus: ICycleStatus = {
    cycleId,
    trainingId,
    finishedTimestamp,
    startTimestamp,
    sessions,
  };
  return cycleStatus;
};

export const filterBySyncTimestamp = <T extends IClientDBSync>(data: T[]) => filter(data, ['syncTimestamp', 0]);

export const updateCycles = (existingCycles: ICycleClientStatus[], updatedCycle: ICycleClientStatus) => {
  const updatedCycles = [...existingCycles];
  const index = findIndex(updatedCycles, { cycleId: updatedCycle.cycleId });
  if (index < 0) {
    updatedCycles.push(updatedCycle);
    return sortBy(updatedCycles, 'cycleId');
  }
  updatedCycles.splice(index, 1, updatedCycle);
  return sortBy(updatedCycles, 'cycleId');
};

export const updateSessions = (
  existingSessions: ISessionClientStatus[],
  updatedSession: ISessionClientStatus | undefined
) => {
  const updatedSessions = [...existingSessions];
  if (updatedSession) {
    const index = findIndex(updatedSessions, { cycleId: updatedSession.cycleId, sessionId: updatedSession.sessionId });
    if (index < 0) {
      updatedSessions.push(updatedSession);
      return sortBy(updatedSessions, ['cycleId', 'sessionId']);
    }
    updatedSessions.splice(index, 1, updatedSession);
  }
  return sortBy(updatedSessions, ['cycleId', 'sessionId']);
};

export const updateWorkouts = (existingWorkouts: IWorkoutClientStatus[], updatedWorkouts: IWorkoutClientStatus[]) => {
  const workouts = [...existingWorkouts];
  for (const workout of updatedWorkouts) {
    const { cycleId, sessionId, index } = workout;
    const existingIndex = findIndex(workouts, {
      cycleId,
      sessionId,
      index,
    });
    if (existingIndex < 0) {
      // if workout does not exist - add it
      workouts.push(workout);
      continue;
    }
    // otherwise replace with new
    workouts.splice(existingIndex, 1, workout);
  }
  return workouts;
};

export const getCycleSessionMapForWorkouts = (workoutsToSync: IWorkoutClientStatus[]) => {
  // create mapping cycleId -> sessionIds
  const cycleMap: TCycleSyncMap = new Map();
  for (const workoutToSync of workoutsToSync) {
    const { cycleId, sessionId } = workoutToSync;
    let sessions = cycleMap.get(cycleId);
    if (!sessions) {
      sessions = new Set<number>();
    }
    sessions.add(sessionId);
    cycleMap.set(cycleId, sessions);
  }
  return cycleMap;
};

export const updateCycleSessionMapForSessions = (cycleMap: TCycleSyncMap, sessionsToSync: ISessionClientStatus[]) => {
  for (const sessionToSync of sessionsToSync) {
    const { cycleId, sessionId } = sessionToSync;
    let sessions = cycleMap.get(cycleId);
    if (!sessions) {
      sessions = new Set<number>();
    }
    sessions.add(sessionId);
    cycleMap.set(cycleId, sessions);
  }
  return cycleMap;
};

export const updateCycleSessionMapForCycles = (cycleMap: TCycleSyncMap, cyclesToSync: ICycleClientStatus[]) => {
  for (const cycleToSync of cyclesToSync) {
    let sessions = cycleMap.get(cycleToSync.cycleId);
    if (!sessions) {
      sessions = new Set<number>();
    }
    cycleMap.set(cycleToSync.cycleId, sessions);
  }
  return cycleMap;
};

export const markAsSync = <T extends IClientDBSync>(data: T) => {
  const syncData: T = {
    ...data,
    syncTimestamp: DateUtils.getUnixTimestamp(),
  };
  return syncData;
};

export const createNewWorkoutStatus = (workoutId: number, index: number): IWorkoutStatus => ({
  workoutId,
  index,
  finishedTimestamp: 0,
  status: ETrainingStatus.None,
});

export const createNewAssessmentSessionStatus = (workouts: IWorkoutStatus[]): ISessionStatus => ({
  sessionId: ASSESSMENT_SESSION_ID,
  domainId: ASSESSMENT_DOMAIN_ID,
  finishedTimestamp: 0,
  status: ETrainingStatus.None,
  workouts,
});

export const createCycle = (cycleId: number, sessions: ISessionStatus[]): ICycleStatus => ({
  trainingId: TRAINING_ID,
  cycleId,
  startTimestamp: -1,
  finishedTimestamp: 0,
  sessions: [...sessions],
});

export const createCycleWithAssessment = (cycleId: number, config: IAssessmentConfigOPXP, addCFQ: boolean) => {
  const steps = config.assessment_ids[ASSESSMENT_SESSION_ID];
  const workouts = steps.map((step, index) => createNewWorkoutStatus(step.id, index));
  if (addCFQ) {
    const lastAssessmentIndex = workouts.length - 1;
    for (let index = 1; index < 26; index++) {
      const workoutId = 10000 + index;
      const workoutIndex = lastAssessmentIndex + index;
      workouts.push(createNewWorkoutStatus(workoutId, workoutIndex));
    }
  }
  const session = createNewAssessmentSessionStatus(workouts);
  return createCycle(cycleId, [session]);
};

export const isWorkoutActive = (workout: IWorkoutClientStatus) =>
  workout.status === ETrainingStatus.None || workout.status === ETrainingStatus.Ongoing;

export const isWorkoutCompleted = (workout: IWorkoutClientStatus) =>
  workout.status === ETrainingStatus.Finished || workout.status === ETrainingStatus.Skipped;

export const isWorkoutFinished = (workout: IWorkoutClientStatus) =>
  workout.status === ETrainingStatus.Finished || workout.workoutId === 1234; // 1234 workout can be skipped because of previous result

export const isCFQWorkout = (workout: IWorkoutClientStatus) => workout.workoutId >= 10000;

export const isNonSkippedCFQWorkout = (workout: IWorkoutClientStatus) =>
  workout.workoutId >= 10000 && workout.status !== ETrainingStatus.Skipped;

export const stripChallenge = (workoutId: number) => (isChallengeWorkoutId(workoutId) ? workoutId - 2000 : workoutId);

export const findFirstUnfinishedWorkout = (workouts: IWorkoutClientStatus[]) => find(workouts, isWorkoutActive);

export const findFirstFinishedWorkout = (workouts: IWorkoutClientStatus[]) => find(workouts, isWorkoutCompleted);

export const removeCFQWorkouts = (workouts: IWorkoutClientStatus[]) => reject(workouts, isCFQWorkout);

export const filterCFQWorkouts = (workouts: IWorkoutClientStatus[]) => filter(workouts, isCFQWorkout);

export const filterNonSkippedCFQWorkouts = (workouts: IWorkoutClientStatus[]) =>
  filter(workouts, isNonSkippedCFQWorkout);

export const findRelativeNonSkippedWorkoutIndex = (workouts: IWorkoutClientStatus[], workoutId: number) =>
  findIndex(workouts, (workout) => workout.workoutId === workoutId && workout.status !== ETrainingStatus.Skipped);

export const findRelativeActiveWorkoutIndex = (workouts: IWorkoutClientStatus[], workoutId: number) =>
  findIndex(workouts, (workout) => workout.workoutId === workoutId && workout.status === ETrainingStatus.None);

export const findFirstUnfinishedWorkoutIndex = (workouts: IWorkoutClientStatus[]) =>
  findIndex(workouts, isWorkoutActive);

export const allWorkoutsAreFinished = (workouts: IWorkoutClientStatus[]) =>
  findFirstUnfinishedWorkoutIndex(workouts) === -1;

export function replaceCurrentWorkout(workouts: IWorkoutClientStatus[], workoutId: number, workoutIndex?: number) {
  const currentIndex = findFirstUnfinishedWorkoutIndex(workouts);
  if (currentIndex < 0) {
    throw new CyclesError('Can not replace workout: Workout not found or completed!');
  }
  // which workout we are going to replace (current)
  const workoutToBeReplaced = workouts[currentIndex];

  // new workout which will become current
  const workoutToReplaceWith: IWorkoutClientStatus = {
    cycleId: workoutToBeReplaced.cycleId,
    sessionId: workoutToBeReplaced.sessionId,
    finishedTimestamp: 0,
    index: workoutToBeReplaced.index,
    status: ETrainingStatus.None,
    workoutId,
    syncTimestamp: 0,
  };
  const updatedWorkouts = [
    ...workouts.slice(0, currentIndex),
    workoutToReplaceWith,
    ...workouts.slice(currentIndex + 1),
  ];
  if (workoutIndex) {
    // switch workout feature
    // we need to put replaced workout to the place of workout given by index
    if (workoutId !== workouts[workoutIndex].workoutId) {
      throw new CyclesError('WorkoutId from parameter should be same as pointed by workoutIndex');
    }
    const workoutTobeSwitched: IWorkoutClientStatus = {
      ...workoutToBeReplaced,
      index: workouts[workoutIndex].index,
    };
    updatedWorkouts[workoutIndex] = workoutTobeSwitched;
  }
  return updatedWorkouts;
}

export function checkForUnsupportedWorkout(
  workoutsToCheck: IWorkoutClientStatus[],
  knownWorkouts: IWorkoutClientStatus[]
) {
  const knownWorkoutIds = knownWorkouts.map((workout) => workout.workoutId);
  return workoutsToCheck.some((workout) => !knownWorkoutIds.includes(workout.workoutId));
}

export function rewriteCycleAssessment(
  cycleId: number,
  workouts: IWorkoutClientStatus[],
  config: IAssessmentConfigOPXP
) {
  const cfqWorkouts: IWorkoutClientStatus[] = [];
  const steps = config.assessment_ids[ASSESSMENT_SESSION_ID];
  // create a list of workouts out of the config
  let onboardingWorkouts = steps.map((step, index) =>
    createWorkoutStatusClient(cycleId, ASSESSMENT_SESSION_ID, step.id, index, ETrainingStatus.None, 0, 0)
  );
  // sort workouts by index
  const existingWorkouts = sortBy(workouts, 'index');
  // check for completed known workouts and mark unknown as skipped
  const haveUnsupportedWorkouts = checkForUnsupportedWorkout(existingWorkouts, onboardingWorkouts);
  const updatedWorkouts = existingWorkouts.map((workout) => {
    const { index, workoutId } = workout;
    const isActive = isWorkoutActive(workout);
    if (haveUnsupportedWorkouts) {
      //We have unsupported workouts, skip every existing workout that not started and leave completed ones untouched
      if (isActive) {
        if (isCFQWorkout(workout)) {
          // If we have CFQ workouts, we skip them and need to add them to the end
          cfqWorkouts.push(workout);
        }
        return createWorkoutStatusClient(
          cycleId,
          ASSESSMENT_SESSION_ID,
          workoutId,
          index,
          ETrainingStatus.Skipped,
          -1,
          0
        );
      } else {
        return {
          ...workout,
        };
      }
    }
    // known assessment workout
    if (!isActive) {
      // if known workout skipped or finished - remove it from assessment workouts
      onboardingWorkouts = reject(onboardingWorkouts, ['workoutId', workoutId]);
    }
    return {
      ...workout,
    };
  });
  // get biggest index from the last of existing workouts
  let lastIndex = updatedWorkouts[updatedWorkouts.length - 1].index;
  // add the rest of onboarding workouts to the end of the session with proper indices
  onboardingWorkouts.forEach(({ workoutId }) => {
    lastIndex += 1;
    const updatedOnboardingWorkout = createWorkoutStatusClient(
      cycleId,
      ASSESSMENT_SESSION_ID,
      workoutId,
      lastIndex,
      ETrainingStatus.None,
      0,
      0
    );
    updatedWorkouts.push(updatedOnboardingWorkout);
  });
  // Add any CFQ workouts at the end
  cfqWorkouts.forEach(({ workoutId }) => {
    lastIndex += 1;
    const updatedCFQWorkout = createWorkoutStatusClient(
      cycleId,
      ASSESSMENT_SESSION_ID,
      workoutId,
      lastIndex,
      ETrainingStatus.None,
      0,
      0
    );
    updatedWorkouts.push(updatedCFQWorkout);
  });
  return updatedWorkouts;
}

export const findFirstUnfinishedSession = (sessions: ISessionClientStatus[]) => find(sessions, isSessionActive);

export const getTrainingConfig = () => {
  return RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigTraining, TrainingConfig);
};

export const getCustomCycleIntensity = (cycleId: number) => {
  const trainingConfig = getTrainingConfig();
  if (!trainingConfig) {
    throw new Error('Training configuration is unavailable!');
  }
  const customIntensities = trainingConfig.custom_cycle_intensities;
  const intensity = find(customIntensities, ['cycle_id', cycleId]);
  return intensity?.training_intensity;
};

export const getSelectedIntensity = (intensity: number): ITrainingIntensity => {
  const trainingConfig = getTrainingConfig();
  if (!trainingConfig) {
    throw new Error('Training configuration is unavailable!');
  }
  const intensities = trainingConfig.training_intensity;
  const result = find(intensities, ['intensity', intensity]);
  return result ?? intensities[0];
};

export const cycleHasCustomIntensity = (cycleId: number) => {
  const intensity = getCustomCycleIntensity(cycleId);
  return !!intensity;
};
